// @flow
import ModularUIResponse from "../../modularui/ModularUIResponse";

import ListItemCollection from "../list/ListItemCollection";
import ListDetailModel from "../list/ListDetailModel";
import ActionCollection from "../actions/ActionCollection";
import FilterCollection from "../filters/FilterCollection";
import GroupingModel from "../grouping/GroupingModel";
import ListHeaderModel from "../list/ListHeaderModel";
import ListHref from "../href/ListHref";
import ListItemModel from "../list/ListItemModel";
import PagingModel from "../paging/PagingModel";
import ResourceModel from "../base/ResourceModel";
import SortingModel from "../sorting/SortingModel";
import { HTTP_METHODS, PARAMETER_TYPES } from "../../constants";

import {
  CASEVIEW_LINK,
  HIDE_WHEN_EMPTY,
  SHOW_ONE_RESULT_AS_DETAIL,
} from "../../constants/LayoutHints";

import type { ModularUIModel, IFilter } from "../types";
import type LinkModel from "../links/LinkModel";

/**
 * Defines a list object
 */
export default class ListModel extends ResourceModel {
  _detail: ?ListDetailModel = null;
  _headers: Array<ListHeaderModel>;
  _paging: PagingModel;
  _filterCollection: FilterCollection;
  _sorting: SortingModel;
  _actionCollection: ActionCollection;
  _listItemCollection: ListItemCollection;
  _grouping: GroupingModel;
  _lookupToken: ?string = null;

  /**
   */
  constructor(modularuiResponse: ModularUIResponse) {
    super(modularuiResponse);

    const lookupToken = modularuiResponse?.parameters?.find(
      (param) => param.name === "lookupToken",
    );
    this.lookupToken = lookupToken?.value;
  }

  /**
   */
  get type(): string {
    return "List";
  }

  /**
   */
  static get modelName(): string {
    return "ListModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    const resourceType = data.contributions?.resourcetype ?? "";
    return (
      resourceType.endsWith("List") ||
      resourceType.endsWith("ListPanel") ||
      resourceType.endsWith("RelatedDataStorePanel") ||
      resourceType.endsWith("RelatedDatastorePanel") ||
      [
        "list-related-cases",
        "RecordPanel",
        "EventHistoryPanel",
        "NotePanel",
        "AppointmentPanel",
        "DocumentPanel",
        "AssignmentPanel",
      ].includes(resourceType)
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    if (
      this.layouthint.has(SHOW_ONE_RESULT_AS_DETAIL) &&
      this.listItemCollection.length === 1
    ) {
      return this.listItemCollection.map((listItem) => {
        const listDetailLink = listItem.selflink;
        listDetailLink.targetModel = ListDetailModel;
        return listDetailLink;
      });
    }

    return [];
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>) {
    this.detail = models.find((childModel) => childModel.type === "ListDetail");
  }

  /**
   */
  get requestMethod(): $Keys<typeof HTTP_METHODS> {
    const mustPost = this.filterCollection.some(
      (filter) =>
        filter.parameterType === PARAMETER_TYPES.BODY && filter.hasValue(),
    );
    return mustPost ? HTTP_METHODS.POST : HTTP_METHODS.GET;
  }

  /**
   * Getting the label of the list
   */
  get label(): string {
    return this.contributions.label;
  }

  /**
   * In case of a lookup list, the token being used to request the list
   */
  get lookupToken(): ?string {
    return this._lookupToken;
  }

  /**
   */
  set lookupToken(lookupToken: ?string) {
    this._lookupToken = lookupToken;
  }

  /**
   * Getting the introduction text
   */
  get introtext(): string {
    if (this.data.content) {
      return this.data.content.text.message;
    }

    if (this.contributions.text) {
      return this.contributions.text.message ?? this.contributions.text;
    }

    if (Array.isArray(this.contributions.texts)) {
      return this.contributions.texts[0].text;
    }

    return "";
  }

  /**
   * Getting the results
   */
  get listItemCollection(): ListItemCollection {
    if (!this._listItemCollection) {
      this._listItemCollection = ListItemCollection.createFromList(this);
    }

    return this._listItemCollection;
  }

  /**
   * Set results
   */
  set listItemCollection(listItemCollection: ListItemCollection) {
    this._listItemCollection = listItemCollection;
  }

  /**
   * Getting the detail
   */
  get detail(): ?ListDetailModel {
    return this._detail;
  }

  /**
   * Add detail model to the {ListModel}
   */
  set detail(detail: ?ModularUIModel) {
    if (detail instanceof ListDetailModel) {
      const listitemHref = detail.selfhref;

      const listitem = this.listItemCollection.find((listItem) =>
        listItem.selfhref.equals(listitemHref),
      );

      if (listitem) {
        detail.listitem = listitem;
      }

      this._detail = detail;
    }
  }

  /**
   * Retrieve grouping information
   */
  get grouping(): GroupingModel {
    if (!this._grouping) {
      this._grouping = this.setGrouping();
    }

    return this._grouping;
  }

  /**
   */
  setGrouping(): GroupingModel {
    return new GroupingModel(
      { ...this.data.grouping, dynamicschema: this.data.dynamicschema },
      this.contributions.contexts,
      this.modelOptions,
    );
  }

  /**
   */
  hasGrouping(): boolean {
    return this.grouping.hasGroups();
  }

  /**
   * Check if list has results
   */
  hasResults(): boolean {
    return this.listItemCollection.hasItems;
  }

  /**
   */
  get shouldHide(): boolean {
    return (
      this.layouthint.has(HIDE_WHEN_EMPTY) &&
      !this.hasResults() &&
      this.actionCollection.isEmpty &&
      !this.filterCollection.hasActiveFilters()
    );
  }

  /**
   * Get list item by ID
   */
  getListItemById(id: string | number): ListItemModel | null {
    const decodedId = decodeURIComponent(id.toString());
    return this.listItemCollection.find(
      (result) => result.id.toString() === decodedId,
    );
  }

  /**
   * Get list item by Href
   */
  getListItemByHref(href: ListHref): ListItemModel | null {
    return this.listItemCollection.find((result) =>
      result.selfhref.equals(href),
    );
  }

  /**
   * Getting paging information
   */
  get paging(): PagingModel {
    if (!this._paging) {
      this._paging = this.setPaging();
    }
    return this._paging;
  }

  /**
   */
  setPaging(): PagingModel {
    return new PagingModel(this.data.paging, this.contributions.paging);
  }

  /**
   * Getting sorting information
   */
  get sorting(): SortingModel {
    if (!this._sorting) {
      this._sorting = this.setSorting();
    }

    return this._sorting;
  }

  /**
   */
  setSorting(): SortingModel {
    return new SortingModel(
      this.contributions,
      this.getSortingLabels(),
      this.grouping,
      this.data.sorting,
    );
  }

  /**
   * Getting the filters
   */
  get filterCollection(): FilterCollection {
    if (!this._filterCollection) {
      this._filterCollection = this.setFilters();
    }

    return this._filterCollection;
  }

  /**
   * Set filterCollection
   */
  set filterCollection(filterCollection: FilterCollection | Array<IFilter>) {
    if (Array.isArray(filterCollection)) {
      this._filterCollection.collection = filterCollection;
    } else {
      this._filterCollection = filterCollection;
    }
  }

  /**
   */
  setFilters(): FilterCollection {
    return new FilterCollection(
      this.data.filter,
      {
        listkey: this.key,
        filter: this.contributions.filter,
        contexts: this.contributions.contexts,
        dynamicschema: this.data.dynamicschema,
      },
      this.modelOptions,
    );
  }

  /**
   * Indicates if list results are filtered
   */
  isFiltered(): boolean {
    return this.filterCollection.hasActiveFilters();
  }

  /**
   * Getting actions
   */
  get actionCollection(): ActionCollection {
    if (!this._actionCollection) {
      this._actionCollection = this.setActionCollection();
    }
    return this._actionCollection;
  }

  /**
   */
  setActionCollection(): ActionCollection {
    return new ActionCollection(
      this.data.actions,
      this.contributions.actions,
      this.modelOptions,
    );
  }

  /**
   * Contains this model list data
   */
  hasList(): boolean {
    return this.key != null;
  }

  /**
   * Sets self href from links collection
   */
  getSelfHref(): ListHref {
    const selfLink = this.links ? this.links.getLinkByKey("self") : null;

    if (selfLink !== null) {
      return new ListHref(selfLink.href, this);
    }

    return new ListHref();
  }

  /**
   * Getting the self link of this list
   */
  get selfhref(): ListHref {
    return this.getSelfHref();
  }

  /**
   * Getting the headers of this list
   */
  get headers(): Array<ListHeaderModel> {
    if (!this._headers) {
      this._headers = this.setHeaders();
    }

    return this._headers;
  }

  /**
   * Set initial headers of list
   */
  setHeaders(): Array<ListHeaderModel> {
    const tempHeaders = [];

    if (this.contributions.results) {
      const { results } = this.contributions;

      Object.keys(results).forEach((key) => {
        results[key].attributes.forEach((attribute) => {
          const listHeader = new ListHeaderModel(attribute, this.sorting);

          const exists = tempHeaders.some((tempHeader) =>
            tempHeader.equals(listHeader),
          );

          if (!exists && !listHeader.layouthint.has(CASEVIEW_LINK)) {
            tempHeaders.push(listHeader);
          }
        });
      });
    }

    return tempHeaders;
  }

  /**
   */
  getSortingLabels(): { [string]: string } {
    const sortingLabels: { [string]: string } = {};

    if (this.contributions.results) {
      const { results } = this.contributions;
      Object.keys(results).forEach((key) => {
        results[key].attributes.forEach((attribute) => {
          const attributeKey = Object.keys(attribute)[0];
          sortingLabels[attributeKey] = attribute[attributeKey].label;
        });
      });
    }

    return sortingLabels;
  }

  /**
   * Retrieve all actions by type
   */
  getActionsByType(actionType: string): ActionCollection {
    return this.actionCollection.getActionsByType(actionType);
  }

  /**
   * Retrieves the label of the first additional detail link by resource type
   */
  getAdditionalDetailLabel(resourceType: string): string | null {
    const resultTypes = Object.keys(this.contributions.results);

    for (const resultType of resultTypes) {
      const link = this.contributions.results[resultType]._links?.panel?.find(
        (panelLink) => panelLink.resourcetype === resourceType,
      );
      if (link) {
        return link.label;
      }
    }

    return null;
  }

  /**
   */
  get formdata(): string | null {
    const { formdata } = this.filterCollection;

    if (formdata != null) {
      return JSON.stringify(formdata);
    }

    return null;
  }
}
